import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import {
  useResearchItem, useSaveResearchItem, useTaxonomy,
  useResearchItemTags, useSetItemTags,
  type ResearchAxis,
} from "@/hooks/useResearch";
import { toast } from "sonner";

const AXES: ResearchAxis[] = ["asset_class", "geography", "sector", "theme", "instrument_type", "custom"];

export default function ResearchEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: item } = useResearchItem(id);
  const { data: taxonomy } = useTaxonomy();
  const { data: itemTags } = useResearchItemTags(id);
  const save = useSaveResearchItem();
  const setTags = useSetItemTags();

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [axis, setAxis] = useState<ResearchAxis | "">("");
  const [conviction, setConviction] = useState<string>("");
  const [horizon, setHorizon] = useState("");
  const [portfolio, setPortfolio] = useState<string>("");
  const [tagIds, setTagIds] = useState<string[]>([]);

  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setSummary(item.summary ?? "");
      setBody(item.body_html ?? "");
      setAxis((item.primary_axis as any) ?? "");
      setConviction(item.conviction ? String(item.conviction) : "");
      setHorizon(item.time_horizon ?? "");
      setPortfolio(item.portfolio_level ? String(item.portfolio_level) : "");
    }
  }, [item]);

  useEffect(() => {
    if (itemTags) setTagIds(itemTags.map((t) => t.taxonomy_value_id));
  }, [itemTags]);

  const handleSave = async () => {
    try {
      const saved = await save.mutateAsync({
        id,
        title,
        summary,
        body_html: body,
        body_markdown: body,
        primary_axis: (axis || null) as any,
        conviction: conviction ? Number(conviction) : null,
        time_horizon: horizon || null,
        portfolio_level: portfolio ? Number(portfolio) : null,
      });
      if (saved?.id) {
        await setTags.mutateAsync({ itemId: saved.id, tagIds });
        toast.success("Saved");
        if (!id) navigate(`/partner/research/${saved.id}`);
      }
    } catch (e: any) {
      toast.error(e.message || "Save failed");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <Card>
        <CardContent className="p-4 space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Summary</Label>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Primary axis</Label>
              <Select value={axis} onValueChange={(v) => setAxis(v as any)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {AXES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conviction (1–5)</Label>
              <Input type="number" min={1} max={5} value={conviction} onChange={(e) => setConviction(e.target.value)} />
            </div>
            <div>
              <Label>Time horizon</Label>
              <Input value={horizon} onChange={(e) => setHorizon(e.target.value)} placeholder="e.g. 6–12m" />
            </div>
            <div>
              <Label>Portfolio level (1–5)</Label>
              <Input type="number" min={1} max={5} value={portfolio} onChange={(e) => setPortfolio(e.target.value)} />
            </div>
          </div>

          {taxonomy && taxonomy.length > 0 && (
            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {taxonomy.map((t) => {
                  const active = tagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTagIds((p) => active ? p.filter((x) => x !== t.id) : [...p, t.id])}
                      className={`text-xs px-2 py-1 rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background hover:border-primary"}`}
                    >
                      {t.axis}: {t.value}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label>Body</Label>
            <RichTextEditor content={body} onChange={setBody} />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" onClick={() => navigate("/partner/research")}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
